import { DY_CLIENT_BASE_URL, EcEnv, X_ACCESS_TOKEN, X_CHANNEL } from '@castlery/config';
import { makePersistenceHandles } from '@castlery/shared-persistence-kit';
import type { ExtraArgument } from '@castlery/shared-redux-extra';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { logger } from '@castlery/observability';
// import { getSession } from 'next-auth/react';
// import { webServerSideRefreshToken, webAuthSignOut, webAuth } from '@castlery/utils';

const needAuthenticated = () => {
  if (EcEnv.NEXT_PUBLIC_CHANNEL === 'POS' || EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') return true;
  return false;
};

// create a new mutex
const mutex = new Mutex();

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: EcEnv.NEXT_PUBLIC_API_HOST,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  prepareHeaders: async (headers, { extra, endpoint }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    if (!headers.has(X_ACCESS_TOKEN) && needAuthenticated()) {
      try {
        let token = undefined;
        // if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
        //   const isServer = typeof window === 'undefined';
        //   const token = isServer ? await webAuth() : await getSession();
        //   if (token && !(token as any).error) {
        //     headers.set(X_ACCESS_TOKEN, `${(token as any).access_token}`);
        //   }
        // } else {
        // const { persistenceHandles } = extra as ExtraArgument;
        // token = persistenceHandles?.accessToken?.getItem();
        // if (token) {
        //   headers.set(X_ACCESS_TOKEN, `${token}`);
        // }
        // }
        // TODO web 发布 pla，next auth 暂时关闭
        const { persistenceHandles } = extra as ExtraArgument;
        if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
          token = persistenceHandles?.webAccessToken?.getItem();
        } else {
          token = persistenceHandles?.accessToken?.getItem();
        }
        if (token) {
          headers.set(X_ACCESS_TOKEN, `${token}`);
        }
      } catch (error) {
        logger.error('Failed to set access token in request header', { error });
      }
    }

    if (EcEnv.NEXT_PUBLIC_CHANNEL === 'POS') {
      try {
        if (!headers.has(X_CHANNEL)) {
          headers.set(X_CHANNEL, EcEnv.NEXT_PUBLIC_CHANNEL.toLocaleLowerCase());
        }
      } catch (error) {
        logger.error('Failed to set channel in request header', { error });
      }
    }

    if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
      try {
        if (!headers.has(X_CHANNEL)) {
          headers.set(X_CHANNEL, EcEnv.NEXT_PUBLIC_CHANNEL.toLocaleLowerCase());
        }
      } catch (error) {
        logger.error('Failed to set channel in request header for DY', { error });
      }
    }

    return headers;
  },
});

const baseQueryWithReAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  if (typeof args !== 'string' && !args?.url) {
    return { error: { status: 404, data: 'url not found' } };
  }
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  // TODO 理论上 不要在这里直接引用这个依赖 而是应该通过注入的方式进行控制反转的
  // 因为该方法可能在服务端运行， 这里是无法直接在服务端获取到cookie的
  // 还是期望能在对应的 app 初始化 store 的时候 注入进来
  const persistenceHandles = makePersistenceHandles();
  let refresh_token = null;
  if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
    refresh_token = persistenceHandles?.webRefreshToken?.getItem();
  } else {
    refresh_token = persistenceHandles?.refreshToken?.getItem();
  }
  if (result.error && result.error.status === 401) {
    if (!refresh_token) {
      if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
        persistenceHandles.webAccessToken.removeItem();
      } else {
        persistenceHandles.accessToken.removeItem();
      }
      return result;
    }
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await baseQuery(
          {
            url: '/oauth/token',
            method: 'POST',
            body: { grant_type: 'refresh_token', refresh_token },
          },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          // api.dispatch(tokenReceived(refreshResult.data));
          const { access_token, refresh_token } = refreshResult.data as {
            access_token: string;
            refresh_token: string;
            expires_in: number;
            created_at: number;
            token_type: string;
          };
          if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
            persistenceHandles.webAccessToken.setItem(access_token);
            persistenceHandles.webRefreshToken.setItem(refresh_token);
          } else {
            await persistenceHandles.accessToken.setItem(access_token);
            await persistenceHandles.refreshToken.setItem(refresh_token);
            await persistenceHandles.isLoggedIn.setItem('1');
          }
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
            persistenceHandles.webAccessToken.removeItem();
            persistenceHandles.webRefreshToken.removeItem();
          } else {
            persistenceHandles.accessToken.removeItem();
            persistenceHandles.refreshToken.removeItem();
            persistenceHandles.isLoggedIn.removeItem();
            location.reload();
          }
        }
      } catch (error) {
        console.log('🚀 ~ file: api.ts:111 ~ error:', error);
        if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
          persistenceHandles.webAccessToken.removeItem();
          persistenceHandles.webRefreshToken.removeItem();
        } else {
          persistenceHandles.accessToken.removeItem();
          persistenceHandles.refreshToken.removeItem();
          persistenceHandles.isLoggedIn.removeItem();
          location.reload();
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  } else if (result.error && result.error.status === 403) {
    if (Array.isArray(result.error.data?.errors) && result.error.data?.errors.length > 0) {
      if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
        persistenceHandles.webAccessToken.removeItem();
        persistenceHandles.webRefreshToken.removeItem();
      } else {
        persistenceHandles.accessToken.removeItem();
        persistenceHandles.refreshToken.removeItem();
        persistenceHandles.isLoggedIn.removeItem();
        if (!(api.endpoint === 'getCurrentAdmin')) {
          location.reload();
        }
      }
    } else {
      if (EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB') {
        persistenceHandles.webAccessToken.removeItem();
        persistenceHandles.webRefreshToken.removeItem();
      } else {
        persistenceHandles.accessToken.removeItem();
        persistenceHandles.refreshToken.removeItem();
        persistenceHandles.isLoggedIn.removeItem();
        location.reload();
      }
    }
  }

  return result;
};

const baseQueryWithRetry = retry(baseQueryWithReAuth, { maxRetries: 3 });

export enum tagTypes {
  User = 'User',
  Product = 'Product',
  Variant = 'Variant',
  Order = 'Order',
  Checkout = 'Checkout',
  Gift = 'Gift',
  ServiceList = 'ServiceList',
  Review = 'Review',
  Coupon = 'Coupon',
  SocialUgc = 'SocialUgc',
}
/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: 'castlery',
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery: baseQueryWithRetry,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: Object.values(tagTypes),
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({}),
});
/**
 * search
 */
export const searchApi = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: 'search-castlery',
  baseQuery: fetchBaseQuery({
    baseUrl:
      // EcEnv.NEXT_PUBLIC_CHANNEL === 'POS'
      //   ? EcEnv.NEXT_PUBLIC_API_HOST.replace('//api', '//search')
      //   : EcEnv.NEXT_PUBLIC_API_HOST,
      EcEnv.NEXT_PUBLIC_API_HOST,
    // eslint-disable-next-line
    prepareHeaders: (headers, { extra, endpoint }) => {
      // By default, if we have a token in the store, let's use that for authenticated requests
      if (!headers.has(X_ACCESS_TOKEN) && needAuthenticated()) {
        try {
          const { persistenceHandles } = extra as ExtraArgument;
          const token = persistenceHandles?.accessToken?.getItem();
          if (token) {
            headers.set(X_ACCESS_TOKEN, `${token}`);
          }
        } catch (error) {
          logger.error('Failed to set access token in DY request header', { error });
        }
      }

      if (EcEnv.NEXT_PUBLIC_CHANNEL === 'POS') {
        try {
          if (!headers.has(X_CHANNEL)) {
            headers.set(X_CHANNEL, EcEnv.NEXT_PUBLIC_CHANNEL.toLocaleLowerCase());
          }
        } catch (error) {
          logger.error('Failed to set channel in DY request header', { error });
        }
      }

      return headers;
    },
  }),
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  // baseQuery: baseQueryWithReAuth,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  // tagTypes: Object.values(tagTypes),
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({}),
});

export const yotpoBaseApi = createApi({
  reducerPath: 'yotpo-api',
  baseQuery: fetchBaseQuery({ baseUrl: 'https://loyalty.yotpo.com/api/' }),
  endpoints: () => ({}),
});

// export const enhancedApi = api.enhanceEndpoints({
//   endpoints: () => ({
//     getPost: () => 'test',
//   }),
// });

export const dyApi = createApi({
  reducerPath: 'dy-api',
  baseQuery: fetchBaseQuery({
    baseUrl: DY_CLIENT_BASE_URL,
  }),
  endpoints: () => ({}),
});

// export const storyblokApi = createApi({
//   reducerPath: 'storyblok-api',
//   baseQuery: fetchBaseQuery({
//     baseUrl: 'https://api.storyblok.com/v2/cdn/stories/',
//   }),
//   endpoints: () => ({}),
// });
